import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { RefreshCw } from "lucide-react";
import { getBackendUrl } from "@/lib/backend";
import { toast } from "sonner";
import "./PatDashboard.css";

const API = `${getBackendUrl()}/api`;

const MetricCard = ({ metric }) => (
  <article className="metric-card">
    <small>{metric.label}</small>
    <strong>{metric.value}</strong>
    <span className={metric.tone || ""}>{metric.hint}</span>
  </article>
);

const PatDashboard = () => {
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "executive";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await axios.get(`${API}/pat/overview`);
      setData(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load PAT dashboard data");
      setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const gaugeStyle = useMemo(() => {
    const pct = data?.executive?.gauge_pct ?? 0;
    const color = data?.executive?.gauge_class === "danger" ? "var(--danger)" : data?.executive?.gauge_class === "warning" ? "var(--warning)" : "var(--primary)";
    return {
      className: `gauge${data?.executive?.gauge_class ? ` ${data.executive.gauge_class}` : ""}`,
      style: {
        background: `radial-gradient(circle at center,#fff 0 52%,transparent 53%),conic-gradient(${color} 0 ${pct}%,rgba(37,99,235,.12) ${pct}% 100%)`,
      },
    };
  }, [data?.executive?.gauge_pct, data?.executive?.gauge_class]);

  const donutStyle = useMemo(() => {
    const segments = data?.executive?.donut?.segments;
    if (!segments?.length) return null;
    const colors = { green: "var(--success)", amber: "var(--warning)", red: "var(--danger)" };
    const total = segments.reduce((s, seg) => s + seg.count, 0) || 1;
    let cursor = 0;
    const stops = segments.map((seg) => {
      const share = (seg.count / total) * 100;
      const start = cursor;
      cursor += share;
      return `${colors[seg.color] || "var(--primary)"} ${start}% ${cursor}%`;
    });
    return { background: `conic-gradient(${stops.join(", ")})` };
  }, [data?.executive?.donut?.segments]);

  if (loading) {
    return (
      <div className="content">
        <p style={{ color: "var(--muted)" }}>Loading PAT dashboard…</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="content">
        <p style={{ color: "var(--danger)" }}>
          PAT data unavailable. Ensure PAT-2 Part-6.xlsx and PAT-3 Part-7.xlsx are in data/excel.
        </p>
      </div>
    );
  }

  const { meta, executive, geography, school, subject, grade, outcome, risk } = data;

  return (
    <div className="content">
      <section className="page-header">
        <div>
          <div className="eyebrow">Visual-first redesign · all KPI dictionary items converted into visuals</div>
          <h1>PAT Learning Assessment Dashboard</h1>
          <p>
            Each KPI group is now represented through KPI cards, comparison charts, heatmaps, gauges, ranked bars,
            risk matrices and action cards — not through dictionary tables.
          </p>
        </div>
        <button type="button" className="primary-action" onClick={() => fetchData(true)} disabled={refreshing}>
          <RefreshCw size={14} />
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </section>

      {activeTab === "executive" && (
        <section className="view active" id="executive">
          <div className="hero-card">
            <div className="hero-grid">
              <div>
                <div className="hero-main">
                  <div className={gaugeStyle.className} style={gaugeStyle.style}>
                    <div>
                      <strong>{executive.pat3_avg}%</strong>
                      <span>PAT-3</span>
                    </div>
                  </div>
                  <div className="hero-copy">
                    <h2>{executive.headline}</h2>
                    <p>{executive.summary}</p>
                    <div className="mini-tabs">
                      {executive.chips?.map((chip) => (
                        <span key={chip}>{chip}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="metric-grid two">
                {executive.hero_metrics?.map((m) => (
                  <MetricCard key={m.label} metric={m} />
                ))}
              </div>
            </div>
          </div>

          <div className="metric-grid">
            {executive.metrics?.map((m) => (
              <MetricCard key={m.label} metric={m} />
            ))}
          </div>

          <div className="grid-2">
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>PAT-2 vs PAT-3 performance</h3>
                  <p>Converts Overall PAT Performance and Improvement Rate into a comparison visual.</p>
                </div>
                <span className="pill">Executive KPI</span>
              </div>
              <div className="chart-bars">
                {executive.comparison_bars?.map((bar) => (
                  <div key={bar.label} className={`vbar${bar.class ? ` ${bar.class}` : ""}`}>
                    <i style={{ height: bar.height }} />
                    {bar.label}
                    <br />
                    <b>
                      {bar.value}
                      {bar.suffix || "%"}
                    </b>
                  </div>
                ))}
              </div>
            </article>
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>School movement distribution</h3>
                  <p>Improved, stable and declining school KPIs shown as part-to-whole visual.</p>
                </div>
                <span className="pill">{meta.schools_common?.toLocaleString("en-IN")} schools</span>
              </div>
              <div className="donut-wrap">
                <div className="donut" style={donutStyle}>
                  <div className="donut-center">
                    <strong>{executive.donut?.center_pct}%</strong>
                    <small>Improved</small>
                  </div>
                </div>
                <div className="legend">
                  {executive.donut?.segments?.map((seg) => (
                    <div key={seg.label} className="legend-row">
                      <span>
                        <i className={`dot ${seg.color === "green" ? "green" : seg.color === "amber" ? "amber" : "red"}`} />
                        {seg.label}
                      </span>
                      <b>{seg.count.toLocaleString("en-IN")}</b>
                    </div>
                  ))}
                </div>
              </div>
            </article>
          </div>
        </section>
      )}

      {activeTab === "geography" && (
        <section className="view active" id="geography">
          <div className="view-banner">
            <div>
              <h2>Geography Analytics View</h2>
              <p>District, block and cluster KPIs are displayed as rankings, heatmaps, progress bars and risk visuals.</p>
            </div>
            <div className="mini-tabs">
              <span>District index</span>
              <span>Block index</span>
              <span>Cluster index</span>
            </div>
          </div>
          <div className="metric-grid">
            {geography.metrics?.map((m) => (
              <MetricCard key={m.label} metric={m} />
            ))}
          </div>
          <div className="grid-2">
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>Top performing blocks</h3>
                  <p>Top Performing Blocks and Block Performance Index visualised as ranked bars.</p>
                </div>
                <span className="pill">GEO-02 / GEO-05</span>
              </div>
              <div className="bar-list">
                {geography.top_blocks?.map((row) => (
                  <div key={row.block} className="hbar-row">
                    <div className="hbar-label">{row.block}</div>
                    <div className="hbar-track">
                      <div className="hbar accent" style={{ width: `${row.bar_pct}%` }} />
                    </div>
                    <div className="hbar-value">{row.score}</div>
                  </div>
                ))}
              </div>
            </article>
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>Block risk heatmap</h3>
                  <p>High Risk Blocks and Learning Equity Index shown as colour intensity.</p>
                </div>
                <span className="pill">GEO-07</span>
              </div>
              <div className="heatmap">
                <div className="label" />
                <div>Perf.</div>
                <div>Growth</div>
                <div>Risk</div>
                <div>Equity</div>
                <div>Priority</div>
                {geography.block_heatmap?.flatMap((row) => [
                  <div key={`${row.block}-label`} className="label">
                    {row.block}
                  </div>,
                  <div key={`${row.block}-perf`} className={row.perf_cell}>
                    {row.score}
                  </div>,
                  <div key={`${row.block}-growth`} className={row.growth_cell}>
                    {row.growth_label}
                  </div>,
                  <div key={`${row.block}-risk`} className={row.risk_cell}>
                    {row.risk}
                  </div>,
                  <div key={`${row.block}-equity`} className="cell2">
                    Med
                  </div>,
                  <div key={`${row.block}-priority`} className={row.priority === "P1" ? "cell1" : row.priority === "P4" ? "cell4" : "cell2"}>
                    {row.priority}
                  </div>,
                ])}
              </div>
            </article>
          </div>
        </section>
      )}

      {activeTab === "school" && (
        <section className="view active" id="school">
          <div className="view-banner">
            <div>
              <h2>School Drilldown View</h2>
              <p>School KPIs are converted into scorecards, movement waterfalls, consistency views and intervention-priority visuals.</p>
            </div>
            <div className="mini-tabs">
              <span>Performance</span>
              <span>Growth</span>
              <span>Risk</span>
              <span>Priority</span>
            </div>
          </div>
          <div className="metric-grid">
            {school.metrics?.map((m) => (
              <MetricCard key={m.label} metric={m} />
            ))}
          </div>
          <div className="grid-2">
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>School movement waterfall</h3>
                  <p>School Growth Index and Risk Index shown as movement pattern, not as a table.</p>
                </div>
                <span className="pill">SCH-02 / SCH-04</span>
              </div>
              <div className="waterfall">
                {school.waterfall?.map((step) => (
                  <div key={step.label} className={`step${step.class ? ` ${step.class}` : ""}`}>
                    <i style={{ height: step.height }} />
                    {step.label}
                    <br />
                    {step.value}
                  </div>
                ))}
              </div>
            </article>
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>School excellence vs intervention</h3>
                  <p>Excellence Index and Intervention Priority Score as clustered priority bubbles.</p>
                </div>
                <span className="pill">SCH-06 / SCH-07</span>
              </div>
              <div className="bubble-grid">
                {school.priority_bubbles?.map((b) => (
                  <div key={b.label + b.subtitle} className={`bubble${b.class ? ` ${b.class}` : ""}`}>
                    {b.label}
                    <br />
                    {b.subtitle}
                  </div>
                ))}
              </div>
            </article>
          </div>
          <div className="grid-3">
            <article className="insight-card">
              <div className="icon-box">🏫</div>
              <div>
                <h4>Low-score schools</h4>
                <p>Use school visit plan for schools below 40% and 40–60% bands.</p>
              </div>
            </article>
            <article className="insight-card">
              <div className="icon-box">📈</div>
              <div>
                <h4>High-growth schools</h4>
                <p>Identify teachers and practices from high-growth schools for replication.</p>
              </div>
            </article>
            <article className="insight-card">
              <div className="icon-box">⚖️</div>
              <div>
                <h4>Consistency gaps</h4>
                <p>Use subject variance to detect inconsistent school-level learning quality.</p>
              </div>
            </article>
          </div>
        </section>
      )}

      {activeTab === "subject" && (
        <section className="view active" id="subject">
          <div className="view-banner">
            <div>
              <h2>Subject Analytics View</h2>
              <p>Subject mastery, growth, learning gap, difficulty and excellence KPIs converted into charts and cards.</p>
            </div>
            <div className="mini-tabs">
              <span>Mastery</span>
              <span>Growth</span>
              <span>Gap</span>
              <span>Difficulty</span>
            </div>
          </div>
          <div className="metric-grid">
            {subject.metrics?.map((m) => (
              <MetricCard key={m.label} metric={m} />
            ))}
          </div>
          <div className="grid-2">
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>Subject performance ranking</h3>
                  <p>Subject Mastery and Subject Excellence KPIs shown as horizontal bars.</p>
                </div>
                <span className="pill">SUB-01 / SUB-05</span>
              </div>
              <div className="bar-list">
                {subject.ranking?.map((row) => (
                  <div key={row.subject} className="hbar-row">
                    <div className="hbar-label">{row.subject}</div>
                    <div className="hbar-track">
                      <div className={`hbar${row.fill ? ` ${row.fill}` : ""}`} style={{ width: `${row.bar_pct}%` }} />
                    </div>
                    <div className="hbar-value">{row.score}</div>
                  </div>
                ))}
              </div>
            </article>
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>Subject gap to target</h3>
                  <p>Target minus actual visualised as official review gap bars.</p>
                </div>
                <span className="pill">SUB-03</span>
              </div>
              <div className="chart-bars">
                {subject.gap_bars?.map((bar) => (
                  <div key={bar.label} className={`vbar${bar.class ? ` ${bar.class}` : ""}`}>
                    <i style={{ height: bar.height }} />
                    {bar.label}
                    <br />
                    <b>{bar.gap}</b>
                  </div>
                ))}
              </div>
            </article>
          </div>
        </section>
      )}

      {activeTab === "grade" && (
        <section className="view active" id="grade">
          <div className="view-banner">
            <div>
              <h2>Grade Analytics View</h2>
              <p>Grade performance, growth, mastery, equity and risk KPIs shown through grade-wise bars and heatmaps.</p>
            </div>
            <div className="mini-tabs">
              <span>Grade index</span>
              <span>Grade risk</span>
              <span>Grade equity</span>
            </div>
          </div>
          <div className="metric-grid">
            {grade.metrics?.map((m) => (
              <MetricCard key={m.label} metric={m} />
            ))}
          </div>
          <div className="grid-2">
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>Grade-wise performance</h3>
                  <p>Grade Performance Index displayed as rank-ordered bars.</p>
                </div>
                <span className="pill">GRD-01</span>
              </div>
              <div className="bar-list">
                {grade.ranking?.map((row) => (
                  <div key={row.grade} className="hbar-row">
                    <div className="hbar-label">{row.grade}</div>
                    <div className="hbar-track">
                      <div className={`hbar${row.fill ? ` ${row.fill}` : ""}`} style={{ width: `${row.bar_pct}%` }} />
                    </div>
                    <div className="hbar-value">{row.score}</div>
                  </div>
                ))}
              </div>
            </article>
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>Grade-subject heatmap</h3>
                  <p>Grade Risk Index and Grade Equity Index represented by colour intensity.</p>
                </div>
                <span className="pill">GRD-03 / GRD-05</span>
              </div>
              <div className="heatmap">
                <div className="label" />
                <div>Lang.</div>
                <div>Math</div>
                <div>Lang.</div>
                <div>Social</div>
                <div>Risk</div>
                {grade.grade_subject_heatmap?.flatMap((row) => [
                  <div key={`${row.grade}-label`} className="label">
                    {row.grade}
                  </div>,
                  ...(row.cells?.map((cell, i) => (
                    <div key={`${row.grade}-${i}`} className={cell.cell_class}>
                      {cell.value}
                    </div>
                  )) || []),
                  <div key={`${row.grade}-pad`} className="cell3">
                    —
                  </div>,
                  <div key={`${row.grade}-risk`} className={row.risk_cell}>
                    {row.risk}
                  </div>,
                ])}
              </div>
            </article>
          </div>
        </section>
      )}

      {activeTab === "outcome" && (
        <section className="view active" id="outcome">
          <div className="view-banner">
            <div>
              <h2>Learning Outcome Analytics View</h2>
              <p>Outcome mastery, growth, gap, critical outcome count, strong outcome count and priority index shown as visuals.</p>
            </div>
            <div className="mini-tabs">
              <span>Mastery</span>
              <span>Gap</span>
              <span>Priority</span>
            </div>
          </div>
          <div className="metric-grid">
            {outcome.metrics?.map((m) => (
              <MetricCard key={m.label} metric={m} />
            ))}
          </div>
          <div className="grid-2">
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>Critical learning outcomes</h3>
                  <p>Lowest outcome mastery shown as remediation priority ranking.</p>
                </div>
                <span className="pill">LO-01 / LO-04</span>
              </div>
              <div className="bar-list">
                {outcome.critical_outcomes?.map((row) => (
                  <div key={row.label} className="hbar-row">
                    <div className="hbar-label">{row.label}</div>
                    <div className="hbar-track">
                      <div className="hbar danger" style={{ width: `${row.bar_pct}%` }} />
                    </div>
                    <div className="hbar-value">{row.score}</div>
                  </div>
                ))}
              </div>
            </article>
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>Outcome priority bubbles</h3>
                  <p>Weighted gap score converted into visual action priority.</p>
                </div>
                <span className="pill">LO-06</span>
              </div>
              <div className="bubble-grid">
                {outcome.priority_bubbles?.map((b) => (
                  <div key={b.label + b.subtitle} className={`bubble${b.class ? ` ${b.class}` : ""}`}>
                    {b.label}
                    <br />
                    {b.subtitle}
                  </div>
                ))}
              </div>
            </article>
          </div>
          <div className="grid-3">
            <article className="insight-card">
              <div className="icon-box">🎯</div>
              <div>
                <h4>Outcome gap index</h4>
                <p>Use target-actual gaps to decide remediation sequencing.</p>
              </div>
            </article>
            <article className="insight-card">
              <div className="icon-box">✍️</div>
              <div>
                <h4>Writing outcomes</h4>
                <p>Theme expansion and formal writing need model answers and rubrics.</p>
              </div>
            </article>
            <article className="insight-card">
              <div className="icon-box">🧮</div>
              <div>
                <h4>Applied math outcomes</h4>
                <p>Use worked examples and weekly practice for tax/profit-loss problems.</p>
              </div>
            </article>
          </div>
        </section>
      )}

      {activeTab === "risk" && (
        <section className="view active" id="risk">
          <div className="view-banner">
            <div>
              <h2>Risk & Intervention View</h2>
              <p>Every risk KPI is shown as a red/amber/green visual, priority score, intervention coverage and action queue.</p>
            </div>
            <div className="mini-tabs">
              <span>Critical</span>
              <span>Amber</span>
              <span>Green</span>
              <span>Action</span>
            </div>
          </div>
          <div className="risk-matrix">
            {risk.matrix?.map((box) => (
              <div key={box.title} className={`risk-box ${box.tone}`}>
                <h4>{box.title}</h4>
                <strong>{typeof box.value === "number" ? box.value.toLocaleString("en-IN") : box.value}</strong>
                <p>{box.text}</p>
              </div>
            ))}
          </div>
          <div className="grid-2">
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>High-risk blocks by intervention need</h3>
                  <p>High-risk blocks, high-risk outcomes and remediation impact potential visualised together.</p>
                </div>
                <span className="pill">RSK-05 / RSK-06 / RSK-07</span>
              </div>
              <div className="bar-list">
                {risk.intervention_blocks?.map((row) => (
                  <div key={row.block} className="hbar-row">
                    <div className="hbar-label">{row.block}</div>
                    <div className="hbar-track">
                      <div className={`hbar${row.fill ? ` ${row.fill}` : ""}`} style={{ width: `${row.bar_pct}%` }} />
                    </div>
                    <div className="hbar-value">{row.priority}</div>
                  </div>
                ))}
              </div>
            </article>
            <article className="panel">
              <div className="panel-head">
                <div>
                  <h3>Action queue for officials</h3>
                  <p>Intervention Priority Schools converted into official-review action cards.</p>
                </div>
                <span className="pill">Next 30 days</span>
              </div>
              {risk.actions?.map((action) => (
                <div key={action.step} className="insight-card">
                  <div className="icon-box">{action.step}</div>
                  <div>
                    <h4>{action.title}</h4>
                    <p>{action.text}</p>
                  </div>
                </div>
              ))}
            </article>
          </div>
        </section>
      )}
    </div>
  );
};

export default PatDashboard;
